import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Button, Typography, TextField, Autocomplete } from '@mui/material';
import _ from 'lodash';
import { Address } from '../../../types/Address';
import AddressForm, { BLANK_ADDRESS, REQUIRED_FIELDS } from '../../common/Address/AddressForm';

interface EventAddressModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (address: Address) => void;
  addressToEdit: Address | null;
  accountAddresses: Address[];
}

const OTHER_ADDRESS_TYPE = 'Other';

const EventAddressModal: React.FC<EventAddressModalProps> = ({ isOpen, onCancel, onSave, addressToEdit, accountAddresses }) => {
  const [address, setAddress] = useState<Address>(BLANK_ADDRESS);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  useEffect(() => {
    if (addressToEdit) {
      const matchedAddress = _.find(accountAddresses, { uuid: addressToEdit.uuid });
      setAddress(addressToEdit);
      setSelectedType(matchedAddress ? matchedAddress.type_value : OTHER_ADDRESS_TYPE);
    } else {
      setAddress(BLANK_ADDRESS);
      setSelectedType(null);
    }
  }, [isOpen, addressToEdit, accountAddresses]);

  const handleTypeChange = (newValue: { label: string; uuid: string }) => {
    if (newValue.uuid === _.lowerCase(OTHER_ADDRESS_TYPE)) {
      setAddress({
        ...BLANK_ADDRESS,
        type_value: OTHER_ADDRESS_TYPE,
        type_uuid: '',
      });
      setSelectedType(OTHER_ADDRESS_TYPE);
    } else {
      const matchedAddress = _.find(accountAddresses, { uuid: newValue.uuid });
      if (matchedAddress) {
        setAddress(matchedAddress);
        setSelectedType(matchedAddress.type_value || '');
      }
    }
  };

  const isAddressDisabled = selectedType !== OTHER_ADDRESS_TYPE;

  const addressOptions = _.map(accountAddresses, (addr) => ({
    label: addr.type_value,
    uuid: addr.uuid,
  }));

  const isFormInvalid = REQUIRED_FIELDS.some((field) => !_.trim((address as any)[field]));

  const handleSubmit = () => {
    if (!isFormInvalid) {
      onSave(address);
      onCancel();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography variant="h6" sx={{ m: 2 }}>
        {addressToEdit ? 'Edit Address' : 'Add Address'}
      </Typography>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={[...addressOptions, { label: OTHER_ADDRESS_TYPE, uuid: _.lowerCase(OTHER_ADDRESS_TYPE) }]}
          value={
            selectedType ? { label: selectedType, uuid: selectedType === OTHER_ADDRESS_TYPE ? _.lowerCase(OTHER_ADDRESS_TYPE) : address.uuid || '' } : null
          }
          onChange={(_, newValue) => handleTypeChange(newValue || { label: '', uuid: '' })}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          renderInput={(params) => <TextField {...params} label="Address Type" required />}
        />

        <AddressForm address={address} setAddress={setAddress} isDisabled={isAddressDisabled} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isFormInvalid}>
          {addressToEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventAddressModal;
